import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAudio } from "../lib/audio";
import { HOTSPOTS, MAP_IMG } from "../lib/mapData";
import { Volume2, Palette, VolumeOff, Sparkles } from "lucide-react";
import SEO from "../components/SEO";
import Lightbox from "../components/Lightbox";
import SayItBox from "../components/SayItBox";

export default function Story() {
  const [chars, setChars] = useState([]);
  const [flipped, setFlipped] = useState({});
  const [hotspot, setHotspot] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { images, index, alt }
  const { playClip } = useAudio();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/characters").then(({ data }) => setChars(data)).catch(() => {});
  }, []);

  const toggle = (slug) => setFlipped((f) => ({ ...f, [slug]: !f[slug] }));
  const hotspotChar = hotspot ? chars.find((c) => c.slug === hotspot.char) : null;

  return (
    <main className="pt-24 pb-12 bg-foam-grad min-h-screen" data-testid="story-page">
      <SEO title="Meet the Sea Stars" description="Eleven Sea Stars from Stingray Cay — meet Myrtle, Ray, Ollie, and the whole crew of camp characters." />
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <header className="text-center mb-10">
          <h1 className="font-accent text-5xl md:text-6xl font-bold">Meet the Sea Stars of Stingray Cay</h1>
          <p className="text-[#4a5568] mt-3 max-w-2xl mx-auto">Tap any card to flip it and meet our crew. Each one has a wave to ride and a way to be brave.</p>
          <Link
            to="/story-quest"
            className="inline-flex items-center gap-2 mt-5 px-5 py-3 rounded-full bg-gradient-to-r from-[#7fcfc7] to-[#5a8a6f] text-white font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition"
            data-testid="story-page-quest-cta"
          >
            <Sparkles className="w-5 h-5" /> Start the Story Quest — find your Sea Star
          </Link>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {chars.map((c) => (
            <div key={c.slug} id={c.slug} className="relative pt-14" data-testid={`character-card-${c.slug}`}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const allImgs = chars.map((x) => x.image_url).filter(Boolean);
                  const idx = allImgs.indexOf(c.image_url);
                  setLightbox({ images: allImgs, index: idx >= 0 ? idx : 0, alt: c.name });
                }}
                className="absolute left-1/2 -translate-x-1/2 -top-4 z-10 gradient-ring animate-bob cursor-zoom-in"
                style={{ width: 132, height: 132 }}
                aria-label={`View larger portrait of ${c.name}`}
                data-testid={`character-portrait-${c.slug}`}
              >
                <img src={c.image_url} alt={c.name} className="w-full h-full rounded-full object-contain bg-[#fffbf3]" />
              </button>
              <div className="card-soft p-6 pt-20 text-center min-h-[320px]">
                <h3 className="font-accent text-2xl font-bold">{c.name}</h3>
                <p className="text-xs uppercase tracking-widest text-[#7cbf94] font-bold mt-1">{c.role}</p>
                {!flipped[c.slug] ? (
                  <>
                    <p className="text-[#4a5568] mt-3 leading-relaxed">{c.bio}</p>
                    <button onClick={() => toggle(c.slug)} className="mt-4 btn-ghost text-sm" data-testid={`flip-${c.slug}`}>More about me →</button>
                  </>
                ) : (
                  <div className="text-left mt-3 space-y-2 text-[#4a5568]">
                    <p><b className="text-[#5a8a6f]">Species:</b> {c.species}</p>
                    <p><b className="text-[#5a8a6f]">W.A.V.E. value:</b> {waveLabel(c.wave_value)}</p>
                    <p><b className="text-[#5a8a6f]">Fun fact:</b> {c.fun_fact}</p>
                    <button onClick={() => toggle(c.slug)} className="btn-ghost text-sm" data-testid={`flip-back-${c.slug}`}>← Back</button>
                  </div>
                )}
                <div className="mt-5 flex flex-wrap gap-2 justify-center">
                  {c.voice_id ? (
                    <button
                      onClick={() => {
                        const url = `${process.env.REACT_APP_BACKEND_URL}/api/voice/character/${c.slug}/greeting`;
                        const a = new Audio(url); a.play().catch(() => {});
                      }}
                      className="btn-ghost text-xs"
                      data-testid={`voice-${c.slug}`}
                    ><Volume2 className="w-4 h-4" />Hear my voice</button>
                  ) : c.audio_url ? (
                    <button onClick={() => playClip(c.audio_url)} className="btn-ghost text-xs" data-testid={`voice-${c.slug}`}><Volume2 className="w-4 h-4" />Hear my voice</button>
                  ) : (
                    <button disabled title="Coming soon" className="btn-ghost text-xs opacity-50 cursor-not-allowed" data-testid={`voice-${c.slug}`}><VolumeOff className="w-4 h-4" />Hear my voice</button>
                  )}
                  <Link to={`/downloads/color-${c.slug}`} className="btn-ghost text-xs"><Palette className="w-4 h-4" />Color me</Link>
                </div>
                {c.voice_id && <SayItBox slug={c.slug} characterName={c.name} />}
              </div>
            </div>
          ))}
        </div>

        {/* Map */}
        <header className="text-center mb-6">
          <h2 className="font-accent text-4xl md:text-5xl font-bold">Map of Stingray Cay</h2>
          <p className="text-[#4a5568] mt-2">Tap a glowing spot to peek inside.</p>
        </header>
        <div className="relative rounded-[28px] overflow-hidden shadow-2xl border-4 border-white" data-testid="cay-map">
          <button
            type="button"
            onClick={() => setLightbox({ images: [MAP_IMG], index: 0, alt: "Stingray Cay map" })}
            className="block w-full cursor-zoom-in"
            aria-label="View larger map"
            data-testid="map-zoom"
          >
            <img src={MAP_IMG} alt="Stingray Cay map" className="w-full h-auto block" />
          </button>
          {HOTSPOTS.map((h) => (
            <button key={h.id} onClick={() => setHotspot(h)} className="absolute w-7 h-7 -ml-3.5 -mt-3.5 rounded-full bg-white/80 border-2 border-[#f0a988] shadow-md hover:scale-125 transition" style={{ left: `${h.x}%`, top: `${h.y}%` }} aria-label={h.title} data-testid={`map-hotspot-${h.id}`}>
              <span className="absolute inset-0 rounded-full animate-ping bg-[#f0a988]/50" />
            </button>
          ))}
        </div>

        {hotspot && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setHotspot(null)} data-testid="map-modal">
            <div className="bg-white rounded-[24px] max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-3">
                {hotspotChar && <div className="gradient-ring" style={{ width: 64, height: 64 }}><img src={hotspotChar.image_url} alt="" className="w-full h-full rounded-full object-contain bg-[#fffbf3]" /></div>}
                <div>
                  <h3 className="font-accent text-2xl font-bold">{hotspot.title}</h3>
                  {hotspotChar && <div className="text-sm text-[#7cbf94]">With {hotspotChar.name}</div>}
                </div>
              </div>
              <p className="text-[#4a5568] leading-relaxed">{hotspot.desc}</p>
              {hotspot.activity && (
                <div className="bg-[#eef9fb] rounded-2xl px-4 py-3 mt-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#f0a988] shrink-0" />
                  <div className="text-sm text-[#3a4a55]">Play <b>{hotspot.activity_label}</b></div>
                </div>
              )}
              <div className="flex gap-2 mt-4 flex-wrap">
                {hotspotChar?.voice_id && (
                  <button
                    onClick={() => {
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/voice/character/${hotspotChar.slug}/greeting`;
                      new Audio(url).play().catch(() => {});
                    }}
                    className="btn-secondary text-sm"
                    data-testid={`map-voice-${hotspotChar.slug}`}
                  ><Volume2 className="w-4 h-4" />Hear them</button>
                )}
                {hotspot.activity && (
                  <button onClick={() => { setHotspot(null); nav(`/activities?game=${hotspot.activity}`); }} className="btn-primary flex-1 justify-center" data-testid={`map-play-${hotspot.activity}`}>Play game →</button>
                )}
                {hotspotChar && <Link to={`/story#${hotspotChar.slug}`} onClick={() => setHotspot(null)} className="btn-ghost text-sm">See bio</Link>}
              </div>
            </div>
          </div>
        )}

        {lightbox && (
          <Lightbox images={lightbox.images} index={lightbox.index} alt={lightbox.alt} onClose={() => setLightbox(null)} />
        )}
      </div>
    </main>
  );
}

function waveLabel(l) {
  const m = { W: "W — Welcome Curiosity", A: "A — Act with Kindness", V: "V — Value Teamwork", E: "E — Encourage Others" };
  return m[l] || l;
}
